import React from 'react';
import { Box, Typography, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import type { TransformedElement } from '@/app/queries/evaluationProtocolQueries';
import { EvaluationProtocolElementType } from '@/api-gen/api.schemas';

interface ElementCheckboxProps {
  element: TransformedElement;
  isSelected: boolean;
  onToggle: (element: TransformedElement) => void;
}

const ElementCheckbox: React.FC<ElementCheckboxProps> = React.memo(({
  element,
  isSelected,
  onToggle
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);

  const getElementTypeDisplayName = (type?: EvaluationProtocolElementType): string => {
    switch (type) {
      case EvaluationProtocolElementType.Numeric:
        return translateCommon(nameof.full<I18nCommonNs>((n) => n.elementTypes.Numerical));
      case EvaluationProtocolElementType.FreeText:
        return translateCommon(nameof.full<I18nCommonNs>((n) => n.elementTypes.Text));
      case EvaluationProtocolElementType.MultipleChoice:
        return translateCommon(nameof.full<I18nCommonNs>((n) => n.elementTypes.MultipleChoice));
      case EvaluationProtocolElementType.YesNo:
        return translateCommon(nameof.full<I18nCommonNs>((n) => n.elementTypes.Boolean));
      default:
        return String(type);
    }
  };

  const handleToggle = React.useCallback(() => {
    onToggle(element);
  }, [element, onToggle]);

  return (
    <Box
      onClick={handleToggle}
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        p: 2,
        transition: 'all 0.15s ease',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: isSelected ? 'primary.50' : 'grey.50'
        }
      }}
    >
      <Checkbox
        checked={isSelected}
        onChange={handleToggle}
        onClick={(e) => e.stopPropagation()}
        sx={{
          p: 0,
          color: 'action.active',
          '&.Mui-checked': { color: 'primary.main' }
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: isSelected ? 600 : 500, 
            lineHeight: 1.5,
            color: 'text.primary'
          }}
        >
          {element.description || translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.unnamedElement))}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary',
            mt: 0.5 
          }}
        >
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.type))}: {getElementTypeDisplayName(element.type)}
        </Typography>
      </Box>
    </Box>
  );
});

ElementCheckbox.displayName = 'ElementCheckbox';

export default ElementCheckbox;
